import React from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiCheckCircle, FiClock } from 'react-icons/fi';
import type { IconType } from 'react-icons';
import AppBrand from '@core/components/AppBrand';
import AppFooter from '@core/components/AppFooter';
import {
  LANDING_HEADLINE,
  LANDING_OVERVIEW_EYEBROW,
  LANDING_TAGLINE,
  LOGIN_CTA_LABEL,
  SIGNUP_CTA_LABEL,
} from '@core/config/branding';
import './landing.css';

const workflowStages = [
  'Submission',
  'Review',
  'Sign-off',
  'Summary',
  'Analytics',
  'Strategic Impact',
];

const REPORTING_PROGRESS_PERCENT = 75;

const overviewStats: {
  label: string;
  value: string;
  accent: 'blue' | 'gold' | 'red';
  icon: IconType;
}[] = [
  { label: 'Pending Review', value: '18', accent: 'blue', icon: FiClock },
  { label: 'Approved', value: '42', accent: 'gold', icon: FiCheckCircle },
  { label: 'Attention Needed', value: '07', accent: 'red', icon: FiAlertCircle },
];

const LandingPage: React.FC = () => {
  return (
    <div className="landing-page min-vh-100 d-flex flex-column">
      <header className="landing-header" aria-label="Landing page header">
        <div className="landing-container landing-header__inner">
          <AppBrand className="landing-brand" />
          <nav className="landing-header__actions" aria-label="Account access">
            <Link to="/register" className="landing-header__signup landing-header__signup--desktop">
              {SIGNUP_CTA_LABEL}
            </Link>
            <Link to="/login" className="btn btn-primary landing-header__login">
              {LOGIN_CTA_LABEL}
            </Link>
          </nav>
        </div>
      </header>

      <main id="main" className="landing-main flex-grow-1" tabIndex={-1}>
        <section className="landing-hero">
          <div className="landing-container">
            <div className="landing-hero__content">
              <p className="landing-eyebrow">Reporting made clearer</p>
              <h1>{LANDING_HEADLINE}</h1>
              <p className="landing-hero__lead">{LANDING_TAGLINE}</p>
              <Link to="/register" className="landing-hero__signup">
                {SIGNUP_CTA_LABEL}
              </Link>
            </div>

            <div className="landing-visual" aria-label="Submission to strategic impact workflow preview">
              <div className="landing-device">
                <div className="landing-device__frame">
                  <div className="landing-device__screen">
                    <div className="landing-workflow-shell">
                      <div className="landing-workflow-shell__toolbar">
                        <div>
                          <span className="landing-workflow-shell__label">Reporting Period</span>
                          <strong>Submission Cycle Overview</strong>
                        </div>
                        <span className="landing-workflow-shell__status">In progress</span>
                      </div>

                      <div className="landing-workflow-board">
                        {workflowStages.map((stage, index) => (
                          <article className="landing-workflow-step" key={stage}>
                            <span className="landing-workflow-step__index">
                              {String(index + 1).padStart(2, '0')}
                            </span>
                            <strong>{stage}</strong>
                            <span className="landing-workflow-step__line" />
                          </article>
                        ))}
                      </div>

                      <div className="landing-overview-card">
                        <span>{LANDING_OVERVIEW_EYEBROW}</span>
                        <strong>Overview and analytics</strong>
                        <p>
                          Approved submissions are shaped into summaries, analytics, and decision
                          support.
                        </p>

                        <div className="landing-reporting-progress">
                          <div className="landing-reporting-progress__header">
                            <span>Reporting progress</span>
                            <span className="landing-reporting-progress__badge">
                              {REPORTING_PROGRESS_PERCENT}%
                            </span>
                          </div>
                          <div
                            className="landing-reporting-progress__bar"
                            role="progressbar"
                            aria-valuenow={REPORTING_PROGRESS_PERCENT}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label="Reporting progress"
                          >
                            <span style={{ width: `${REPORTING_PROGRESS_PERCENT}%` }} />
                          </div>
                        </div>

                        <div className="landing-overview-stats">
                          {overviewStats.map((stat) => {
                            const Icon = stat.icon;
                            return (
                              <div
                                className={`landing-overview-stat landing-overview-stat--${stat.accent}`}
                                key={stat.label}
                              >
                                <span
                                  className={`landing-overview-stat__icon landing-overview-stat__icon--${stat.accent}`}
                                >
                                  <Icon size={16} aria-hidden />
                                </span>
                                <div className="landing-overview-stat__content">
                                  <strong>{stat.value}</strong>
                                  <span>{stat.label}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="landing-device__base" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <AppFooter />
    </div>
  );
};

export default LandingPage;
